import React, { useState, useEffect } from "react";
import { styled } from "@mui/material/styles";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import {
  Backdrop,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  FormHelperText,
  IconButton,
  Link,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import { Clear, Save, Visibility } from "@mui/icons-material";
import { myExpariencesService } from "../../../services";
import { allVideosContentValid, validURL } from "../utils/utils";
import { LocalizationProvider, DateTimePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import "dayjs/locale/fr";
import dayjs from "dayjs";
import i18next from "i18next";
import {
  updateElement,
  updateLastMainVideo,
  updateModalManager,
} from "../../../reducers/configurator/configuratorSlice";
import { resetLayers } from "../../../reducers/configurator/utils";
import Hourglass from "../../../assets/hourglass.json";
import Lottie from "lottie-react";
import ConfigDataBank from "./ConfigDataBank";
import DndKit from "../../dragNDrop/DndKit";

const PREFIX = "screenForm";

const classes = {
  youtubeFieldContainer: `${PREFIX}-youtubeFieldContainer`,
  linkContainer: `${PREFIX}-linkContainer`,
  marginBottom: `${PREFIX}-marginBottom`,
  linkIconPosition: `${PREFIX}-linkIconPosition`,
  checkboxesContainer: `${PREFIX}-checkboxesContainer`,
  autoplayCheckbox: `${PREFIX}-autoplayCheckbox`,
  pickersContainer: `${PREFIX}-pickersContainer`,
  videosContainer: `${PREFIX}-videosContainer`,
  contentInfosContainer: `${PREFIX}-contentInfosContainer`,
  playlistInformations: `${PREFIX}-playlistInformations`,
  percentProgressIcon: `${PREFIX}-percentProgressIcon`,
  wordBreakStyle: `${PREFIX}-wordBreakStyle`,
  videoUrlContainer: `${PREFIX}-videoUrlContainer`,
  videoUrlTextField: `${PREFIX}-videoUrlTextField`,
  saveButton: `${PREFIX}-saveButton`,
  middleText: `${PREFIX}-middleText`,
  importFilesButton: `${PREFIX}-importFilesButton`,
  ButtonDecoration: `${PREFIX}-ButtonDecoration`,
  dragItemContainer: `${PREFIX}-dragItemContainer`,
  previewIcon: `${PREFIX}-previewIcon`,
  previewIconBackdrop: `${PREFIX}-previewIconBackdrop`,
  deleteIcon: `${PREFIX}-deleteIcon`,
  backdrop: `${PREFIX}-backdrop`,
  videoPlayerContainer: `${PREFIX}-videoPlayerContainer`,
  videoTopBar: `${PREFIX}-videoTopBar`,
  videoTitle: `${PREFIX}-videoTitle`,
  videoPlayer: `${PREFIX}-videoPlayer`,
  clearIconPreview: `${PREFIX}-clearIconPreview`,
  pedestalButtonsContainer: `${PREFIX}-pedestalButtonsContainer`,
  draggableContainer: `${PREFIX}-draggableContainer`,
};

const ScreenFormContainer = styled("div")(({ theme }) => {
  return {
    [`& .${classes.youtubeFieldContainer}`]: {
      margin: "1rem auto",
      maxWidth: "650px",
    },
    [`& .${classes.linkContainer}`]: {
      marginTop: "1rem",
      display: "flex",
      justifyContent: "space-between",
      position: "relative",
    },
    [`& .${classes.marginBottom}`]: {
      marginBottom: "1rem !important",
    },
    [`& .${classes.linkIconPosition}`]: {
      position: "absolute",
      top: "-10px",
      right: "0",
    },
    [`& .${classes.checkboxesContainer}`]: {
      display: "flex",
      justifyContent: "center",
    },
    [`& .${classes.autoplayCheckbox}`]: {
      marginTop: "0.5rem",
      position: "relative",
    },
    [`& .${classes.pickersContainer}`]: {
      width: "100%",
      display: "grid",
      textAlign: "center",
    },
    [`& .${classes.videosContainer}`]: {
      margin: "1rem 0",
      overflowY: "unset",
    },
    [`& .${classes.playlistInformations}`]: {
      display: "flex",
      flexDirection: "column",
      margin: "0.5rem 0.5rem 1.5rem 0",
      height: "80%",
    },
    [`& .${classes.contentInfosContainer}`]: {
      display: "flex",
      flexDirection: "row",
      paddingTop: "0.25rem",
      width: "250px",
    },
    [`& .${classes.percentProgressIcon}`]: {
      transform: "translateY(10px)",
      zIndex: 99,
      background: "#424242",
      position: "relative",
      marginLeft: "0.5rem",
    },
    [`& .${classes.wordBreakStyle}`]: {
      marginLeft: "3rem",
      textOverflow: "ellipsis",
      overflow: "hidden",
      maxWidth: "175px",
      transform: "translateY(13px)",
      whiteSpace: "nowrap",
      color: "#D3AF5F",
      [theme.breakpoints.up("sm")]: {
        maxWidth: "250px",
      },
    },
    [`& .${classes.videoUrlContainer}`]: {
      display: "flex",
      alignItems: "center",
      marginBottom: "0.5rem",
    },
    [`& .${classes.videoUrlTextField}`]: {
      margin: "0.5rem 1rem 0.5rem 0",
    },
    [`& .${classes.saveButton}`]: {
      marginLeft: "1rem !important",
      height: "56px",
    },
    [`& .${classes.middleText}`]: {
      marginTop: "0.5rem",
      display: "flex",
      justifyContent: "center",
    },
    [`& .${classes.importFilesButton}`]: {
      display: "flex",
      justifyContent: "center",
    },
    [`& .${classes.ButtonDecoration}`]: {
      textDecoration: "none",
    },
    [`& .${classes.dragItemContainer}`]: {
      display: "flex",
    },
    [`& .${classes.previewIcon}`]: {
      position: "absolute",
      bottom: "10px",
      right: "10px",
    },
    [`& .${classes.previewIconBackdrop}`]: {
      cursor: "pointer",
      position: "absolute",
      bottom: "5px",
      right: "10px",
    },
    [`& .${classes.deleteIcon}`]: {
      position: "absolute",
      top: "10px",
      right: "10px",
      "&:hover": {
        cursor: "pointer",
      },
    },
    [`& .${classes.backdrop}`]: {
      zIndex: theme.zIndex.drawer + 1,
      color: "#fff",
    },
    [`& .${classes.videoPlayerContainer}`]: {
      padding: "5px 5px 0 5px",
    },
    [`& .${classes.videoTopBar}`]: {
      display: "flex",
      justifyContent: "space-between",
    },
    [`& .${classes.videoTitle}`]: {
      marginBottom: "0.5rem",
      textOverflow: "ellipsis",
      overflow: "hidden",
      maxWidth: "250px",
      whiteSpace: "nowrap",
    },
    [`& .${classes.videoPlayer}`]: {
      width: "100%",
      height: "100%",
      maxWidth: "350px",
    },
    [`& .${classes.clearIconPreview}`]: {
      cursor: "pointer",
    },
    [`& .${classes.pedestalButtonsContainer}`]: {
      display: "flex",
      justifyContent: "space-around",
    },
    [`& .${classes.draggableContainer}`]: {
      marginBottom: "2rem",
      background: "#424242",
      padding: "0.25rem",
      overflow: "auto",
      maxHeight: "300px",
      "&::-webkit-scrollbar": {
        width: "5px",
        height: "5px",
      },
      "&::-webkit-scrollbar-thumb": {
        backgroundColor: theme.palette.primary.main,
        borderRadius: "3px",
      },
      "&::-webkit-scrollbar-track": {
        backgroundColor: "rgb(181 154 79 / 58%)",
        borderRadius: "10px",
      },
    },
  };
});

function CircularProgressWithLabel(props) {
  return (
    <Box position="relative" display="inline-flex">
      <CircularProgress variant="determinate" {...props} />
      <Box
        top={0}
        left={0}
        bottom={0}
        right={0}
        position="absolute"
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        <Typography variant="caption" component="div" color="textSecondary">
          {`${isNaN(Math.round(props.value)) ? 0 : Math.round(props.value)}%`}
        </Typography>
      </Box>
    </Box>
  );
}

const ScreenForm = (props) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const [videoUrl, setVideoUrl] = useState("");
  const [upload, setUpload] = useState({ uploading: false, files: [] });
  const [previewing, setPreviewing] = useState({ open: false, src: null });

  const selectedTile = useSelector((state) => state.configurator.selectedTile);
  const parentLayout = useSelector((state) => state.configurator.parentLayout);
  const myTile = useSelector((state) =>
    selectedTile
      ? state.configurator.grid[parentLayout][selectedTile.row][
          selectedTile.column
        ]
      : null
  );
  const saveTrigger = useSelector(
    (state) => state.configurator.modalManager.saveTrigger
  );
  const lastMainVideo = useSelector(
    (state) => state.configurator.lastMainVideo
  );

  const [state, setState] = useState(null);

  const exparienceId = useSelector(
    (state) => state.myExpariences?.selectedExparience?._id
  );
  const objOverview = useSelector(
    (state) => state.configurator.objectsOverview
  );
  const parentedLayoutsList = useSelector(
    (state) => state.configurator.parentedLayoutsList
  );

  const isAdmin = useSelector(
    (state) => state.user?.currentUser?.account?.role === "ADMIN"
  );

  useEffect(() => {
    if (state) {
      if (
        (state.live &&
          (!state.start ||
            !state.end ||
            !state.link ||
            state.link?.length === 0 ||
            !validURL(state.link))) ||
        (!state.live &&
          (state.links.length === 0 || !allVideosContentValid(state.links)))
      )
        dispatch(updateModalManager({ disableSave: true }));
      else dispatch(updateModalManager({ disableSave: false }));
    }
  }, [state]);

  useEffect(() => {
    if (!upload.uploading) dispatch(updateModalManager({ disableSave: false }));
    else dispatch(updateModalManager({ disableSave: true }));
  }, [upload.uploading]);

  useEffect(() => {
    if (saveTrigger && !upload.uploading) {
      if (myTile[props.layer].mainVideo !== state.mainVideo) {
        let matchingLayoutKey = null;
        Object.keys(parentedLayoutsList).forEach((key) => {
          if (parentedLayoutsList[key].includes(parentLayout))
            matchingLayoutKey = key;
        });
        const existInOld = lastMainVideo.find(
          (item) =>
            item.layout === parentLayout ||
            item.layout === matchingLayoutKey ||
            (parentedLayoutsList.hasOwnProperty(parentLayout) &&
              parentedLayoutsList[parentLayout].includes(item.layout))
        );
        if (existInOld) {
          // exist in same layout
          dispatch(
            updateElement({
              screenOldLayout: existInOld.data.layout,
              layer: existInOld.data.rotation,
              data: { ...existInOld.data, mainVideo: false },
              tile: {
                row: existInOld.startTile.row,
                column: existInOld.startTile.column,
              },
              tileEnd: {
                row: existInOld.endTile.row,
                column: existInOld.endTile.column,
              },
            })
          );
        }
        dispatch(
          updateLastMainVideo({
            layout: parentLayout,
            startTile: {
              row: state.startTile.row,
              column: state.startTile.column,
            },
            endTile: { row: state.endTile.row, column: state.endTile.column },
            data: { ...state },
          })
        );
      }
      dispatch(
        updateElement({
          layer: state.rotation,
          data: {
            ...state,
            links: state.links.map((elem) => ({
              link: elem.link,
              name: elem.name,
            })),
          },
          tile: { row: state.startTile.row, column: state.startTile.column },
          tileEnd: { row: state.endTile.row, column: state.endTile.column },
        })
      );
      dispatch(updateModalManager(resetLayers.modalManager));
    }
  }, [saveTrigger]);

  useEffect(() => {
    if (props.layer) {
      setState({
        ...myTile[props.layer],
        links: myTile[props.layer].links.map((i, index) => ({
          ...i,
          id: `unique-element-${index + 1}`,
        })),
      });
    }
    if (props.state) {
      setState({
        ...props.state,
        links: props.state.links.map((i, index) => ({
          ...i,
          id: `unique-element-${index + 1}`,
        })),
      });
    }
  }, [myTile, props.layer, props.state]);

  const handleProgress = (index, percent) => {
    const tmp = [...upload.files];
    tmp[index].percent = percent;
    setUpload({ ...upload, percents: tmp });
  };

  const getFilesLinks = async (files) => {
    const tmp = [];
    for (let i in files) {
      const res = await myExpariencesService.uploadVideosForScreen(
        exparienceId,
        files[i],
        handleProgress,
        i,
        isAdmin
      );
      tmp.push({
        link: res.data.url,
        name: files[i].name,
        id: `${files[i].name}-${i + 1}`,
      });
    }
    return tmp;
  };

  const uploadVideos = async (files) => {
    const tmp = await getFilesLinks(files);
    const tmpState = [...state.links];
    tmp.forEach((item) => {
      const index = tmpState.findIndex((current) => current.name === item.name);
      tmpState[index] = {
        ...item,
        link: item.link,
      };
    });
    setState({ ...state, links: tmpState });
    if (props.setState) props.setState({ ...state, links: tmpState });
    setUpload({ files: [], percents: [], uploading: false });
  };

  useEffect(() => {
    if (upload.files.length > 0 && upload.uploading) {
      uploadVideos(upload.files);
    }
  }, [upload.uploading]);

  const handleAddFiles = async (files) => {
    const filesArray = Array.from(files);
    const tmpLinks = [...state.links];
    const percents = [];
    filesArray.forEach((file) => {
      tmpLinks.push({ name: file.name });
      percents.push({ percent: 0, name: file.name });
    });
    setUpload({
      uploading: true,
      percents,
      files: filesArray,
    });
    setState({ ...state, links: tmpLinks });
    if (props.setState) props.setState({ ...state, links: tmpLinks });
  };

  const handleSaveVideoUrl = () => {
    const tmpLinks = [...state.links];
    const reducedOverview = objOverview.screens.reduce(
      (prev, current) => [...prev, ...current.links],
      []
    );
    const existingName = reducedOverview.find((item) => item.link === videoUrl);
    if (existingName) {
      tmpLinks.push({
        ...existingName,
        id: `${existingName.name}-${tmpLinks.length + 1}`,
      });
    } else
      tmpLinks.push({
        link: videoUrl,
        id: `${videoUrl}-${tmpLinks.length + 1}`,
      });
    setVideoUrl("");
    setState({ ...state, links: tmpLinks });
    if (props.setState) props.setState({ ...state, links: tmpLinks });
  };

  const handleDeleteElement = (index) => {
    const tmpLinks = [...state.links];
    tmpLinks.splice(index, 1);
    setState({ ...state, links: tmpLinks });
    if (props.setState) props.setState({ ...state, links: tmpLinks });
  };

  const getPercentIcon = (name) => {
    const elem = upload.percents.find((item) => item.name === name);
    const percent = elem ? elem.percent : "100";
    return percent === 100 ? (
      <Lottie
        animationData={Hourglass}
        loop={true}
        style={{
          transform: "translateY(-10px)",
          width: "50px",
          height: "50px",
        }}
      />
    ) : (
      <CircularProgressWithLabel value={percent} />
    );
  };

  const getPercentTitle = (item) => {
    const elem = upload.percents?.find((v) => v.name === item.name);
    return elem?.percent === 100
      ? t("myExpariences.form.videoProcessing")
      : item.name || item.link;
  };

  const handleSaveOrderedArray = (newArr) => {
    setState({ ...state, links: newArr });
    if (props.setState) props.setState({ ...state, links: newArr });
  };

  if (!state) return null;
  else
    return (
      <ScreenFormContainer>
        <div className={classes.youtubeFieldContainer}>
          <Backdrop
            className={classes.backdrop}
            open={previewing.open}
            onClick={() => setPreviewing({ open: false, src: null })}
          >
            <Paper elevation={12} className={classes.videoPlayerContainer}>
              <div className={classes.videoTopBar}>
                <Typography variant="body1" className={classes.videoTitle}>
                  {previewing.name}
                </Typography>
                <div
                  onClick={() => setPreviewing({ open: false, src: null })}
                  className={classes.clearIconPreview}
                >
                  <Clear color="primary" />
                </div>
              </div>
              <video
                className={classes.videoPlayer}
                loop
                preload="auto"
                controls
                autoPlay
                src={previewing.src}
              />
            </Paper>
          </Backdrop>
          {props.layer && (
            <div className={classes.pedestalButtonsContainer}>
              <Button
                variant="contained"
                color="primary"
                onClick={(e) => {
                  setState({ ...state, type: "ipad" });
                }}
                style={{
                  border:
                    props.layer && state.type === "ipad" && "1px solid white",
                  opacity: props.layer && state.type !== "ipad" && "0.5",
                  marginRight: "1rem",
                }}
              >
                {t("myExpariences.form.screenAndSupport")}
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={(e) => {
                  setState({ ...state, type: "screen" });
                }}
                style={{
                  border:
                    props.layer && state.type === "screen" && "1px solid white",
                  opacity: props.layer && state.type !== "screen" && "0.5",
                  marginRight: "1rem",
                }}
              >
                {t("configurator.screen")}
              </Button>
            </div>
          )}
          <div className={classes.checkboxesContainer}>
            <div>
              <FormControlLabel
                control={
                  <Checkbox
                    color={"primary"}
                    onChange={(e) => {
                      setState({ ...state, autoplay: e.target.checked });
                      if (props.setState)
                        props.setState({
                          ...state,
                          autoplay: e.target.checked,
                        });
                    }}
                    checked={state.autoplay}
                  />
                }
                label={
                  <Typography variant="body1">
                    {t("myExpariences.form.autoplay")}
                  </Typography>
                }
                className={classes.autoplayCheckbox}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    color={"primary"}
                    onChange={(e) => {
                      setState({ ...state, mainVideo: e.target.checked });
                      if (props.setState)
                        props.setState({
                          ...state,
                          mainVideo: e.target.checked,
                        });
                    }}
                    checked={state.mainVideo}
                  />
                }
                label={
                  <Typography variant="body1">
                    {t("myExpariences.form.mainVideo")}
                  </Typography>
                }
                className={classes.autoplayCheckbox}
              />
            </div>
            <div>
              <FormControlLabel
                control={
                  <Checkbox
                    color={"primary"}
                    onChange={(e) => {
                      setState({ ...state, mute: e.target.checked });
                      if (props.setState)
                        props.setState({ ...state, mute: e.target.checked });
                    }}
                    checked={state.mute}
                  />
                }
                label={
                  <Typography variant="body1">
                    {t("myExpariences.form.mute")}
                  </Typography>
                }
                className={classes.autoplayCheckbox}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    color={"primary"}
                    onChange={(e) => {
                      setState({ ...state, live: e.target.checked });
                      if (props.setState)
                        props.setState({ ...state, live: e.target.checked });
                    }}
                    checked={state.live || false}
                  />
                }
                label={
                  <Typography variant="body1">
                    {t("myExpariences.form.live")}
                  </Typography>
                }
                className={classes.autoplayCheckbox}
              />
            </div>
            <div>
              <FormControlLabel
                control={
                  <Checkbox
                    color={"primary"}
                    onChange={(e) => {
                      setState({ ...state, loop: e.target.checked });
                      if (props.setState)
                        props.setState({ ...state, loop: e.target.checked });
                    }}
                    checked={state.loop}
                  />
                }
                label={
                  <Typography variant="body1">
                    {t("myExpariences.form.loop")}
                  </Typography>
                }
                className={classes.autoplayCheckbox}
              />
            </div>
          </div>
          {state.live && (
            <>
              <div className={classes.linkContainer}>
                <Typography variant="body1" className={classes.marginBottom}>
                  {t("myExpariences.form.urlScreen")}
                </Typography>
                <IconButton
                  color="primary"
                  aria-label="upload picture"
                  component="span"
                  className={classes.linkIconPosition}
                  onClick={() => window.open(state.link, "_blank")}
                >
                  <Link />
                </IconButton>
              </div>
              <TextField
                label={t("configurator.screenOptionsTitle")}
                onChange={(e) => {
                  setState({ ...state, link: e.target.value });
                  if (props.setState)
                    props.setState({ ...state, link: e.target.value });
                }}
                value={state.link || ""}
                error={state.link?.length > 0 && !validURL(state.link)}
                fullWidth
                variant={"outlined"}
              />
              {state.link?.length > 0 && !validURL(state.link) && (
                <FormHelperText id="validUrl" error>
                  {t("configurator.screenOptionsUrlError")}
                </FormHelperText>
              )}
            </>
          )}
          {state.live && (
            <div className={classes.pickersContainer}>
              <LocalizationProvider
                dateAdapter={AdapterDayjs}
                adapterLocale={i18next.language.substring(0, 2)}
              >
                <DateTimePicker
                  margin="normal"
                  id="from-date-picker-screen"
                  className={classes.fullHW}
                  label={t("myDesign.zones.schedule.form.from")}
                  onChange={(date) => {
                    setState({ ...state, start: date });
                    if (props.setState)
                      props.setState({ ...state, start: date });
                  }}
                  KeyboardButtonProps={{
                    "aria-label": "change date",
                  }}
                  minDate={dayjs(state.start) || dayjs()}
                  value={dayjs(state.start)}
                />
                {state.start && (
                  <DateTimePicker
                    margin="normal"
                    id="to-date-picker-screen"
                    label={t("myDesign.zones.schedule.form.to")}
                    value={dayjs(state.end)}
                    onChange={(date) => {
                      setState({ ...state, end: date });
                      if (props.setState)
                        props.setState({ ...state, end: date });
                    }}
                    KeyboardButtonProps={{
                      "aria-label": "change date",
                    }}
                    minDate={dayjs(state.start)}
                  />
                )}
              </LocalizationProvider>
            </div>
          )}
          <div className={classes.videosContainer}>
            <Typography variant="body1" className={classes.marginBottom}>
              {t("configurator.screenContentTitle")}
            </Typography>
            <input
              type="file"
              accept="video/*"
              multiple
              name="myContentFiles"
              id="icon-button-my-content-video"
              style={{ display: "none" }}
              onClick={(e) => (e.target.value = null)}
              onChange={(e) => {
                handleAddFiles(e.target.files);
              }}
            />
            <label htmlFor="icon-button-my-content-video">
              <Button
                variant="contained"
                color="primary"
                component="span"
                className={classes.importFilesButton}
              >
                {t("configurator.importVideosButton")}
              </Button>
            </label>
            <Typography variant="body1" className={classes.middleText}>
              {t("configurator.orText")}
            </Typography>
            <Box>
              <ConfigDataBank mode="screens" onSelectMedia={setVideoUrl} />
              <div className={classes.videoUrlContainer}>
                <TextField
                  label={t("configurator.videoUrlTitle")}
                  onChange={(e) => setVideoUrl(e.target.value)}
                  className={classes.videoUrlTextField}
                  value={videoUrl}
                  fullWidth
                  variant={"outlined"}
                />
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => handleSaveVideoUrl()}
                  className={classes.saveButton}
                  disabled={
                    videoUrl.length === 0 ||
                    (videoUrl.length > 0 && !validURL(videoUrl))
                  }
                >
                  <Save />
                </Button>
              </div>
            </Box>
            {state.links.length > 0 && (
              <Paper elevation={10} className={classes.draggableContainer}>
                <DndKit
                  mode={"vertical"}
                  customWidth
                  data={state.links}
                  reorderedArray={(newArray) =>
                    handleSaveOrderedArray(newArray)
                  }
                  renderItem={(item, index) => (
                    <Paper
                      elevation={12}
                      key={`screen-form-draggable-${index + 1}`}
                      sx={{
                        height: "75px",
                        padding: "0.25rem",
                        display: "flex",
                        alignItems: "center",
                        background: "#424242",
                      }}
                    >
                      <div className={classes.playlistInformations}>
                        <div className={classes.contentInfosContainer}>
                          <div>
                            {!item.link && (
                              <div className={classes.percentProgressIcon}>
                                {getPercentIcon(item.name)}
                              </div>
                            )}
                          </div>
                          <div className={classes.dragItemContainer}>
                            <Typography
                              variant="h5"
                              className={classes.wordBreakStyle}
                            >
                              {getPercentTitle(item)}
                            </Typography>
                            <Clear
                              color="primary"
                              className={classes.deleteIcon}
                              onClick={() => handleDeleteElement(index)}
                            />
                            {item.link &&
                            !item.link.includes("exparience") &&
                            !item.link.includes(".s3") ? (
                              <a
                                href={item.link}
                                className={classes.ButtonDecoration}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                <Visibility
                                  color="primary"
                                  className={classes.previewIcon}
                                />
                              </a>
                            ) : item.link &&
                              item.link.includes("exparience") &&
                              item.link.includes(".s3") ? (
                              <div
                                className={classes.previewIconBackdrop}
                                onClick={() =>
                                  setPreviewing({
                                    open: true,
                                    src: item.link,
                                    name: item.name || null,
                                  })
                                }
                              >
                                <Visibility color="primary" />
                              </div>
                            ) : null}
                          </div>
                        </div>
                      </div>
                    </Paper>
                  )}
                />
              </Paper>
            )}
          </div>
        </div>
      </ScreenFormContainer>
    );
};

export default ScreenForm;
